/**
 * @file Compliance report selection, CSV download, and print rendering.
 */

import {
    bindNativeFieldBehavior,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {closeForm} from "../../shared/forms/modalShell.js";
import {openStandardForm} from "../../shared/forms/standardFormModal.js";
import {openPrintWindow} from "../../shared/reporting/printWindow.js";
import {openConfirmationModal} from "../../shared/ui/confirmationModal.js";
import {
    validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import {createSelectControl} from "../../shared/ui/controls/selectControl.js";
import {escapeHTML} from "../../shared/utils/contentUtils.js";
import {generateComplianceReport} from "./complianceApi.js";

const PRINT_FIELDS = Object.freeze([
    ["category", "Category"],
    ["trackedItem", "Tracked item"],
    ["trackedItemSource", "Tracked item source"],
    ["itemState", "Item state"],
    ["requirement", "Requirement"],
    ["requirementState", "Requirement state"],
    ["complianceState", "Compliance state"],
    ["performedDate", "Performed/start"],
    ["priorDueDate", "Prior due"],
    ["nextDueDate", "Next due"],
    ["recordedBy", "Recorded by"],
    ["recordedAt", "Recorded at"],
    ["correctedBy", "Last corrected by"],
    ["correctedAt", "Last corrected at"],
    ["supersededBy", "Superseded by"],
    ["supersededAt", "Superseded at"],
    ["source", "Source"],
    ["evidenceAvailable", "Evidence available"],
]);
const OPERATIONAL_PRINT_FIELDS = Object.freeze(PRINT_FIELDS.slice(0, 10));
const PROVENANCE_PRINT_FIELDS = Object.freeze(PRINT_FIELDS.slice(10));

function showReportFailure(error, returnFocusTarget) {
    openConfirmationModal(
        "Report unavailable",
        escapeHTML(error?.message ||
            "The Compliance report could not be generated."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {
            hideCancel: true,
            returnFocusTarget,
        },
    );
}

function focusReportCategoryError(categoryControl) {
    requestAnimationFrame(() => {
        const focusTarget = categoryControl.root.querySelector(
            '[role="combobox"]',
        );
        focusTarget?.focus({preventScroll: true});
        categoryControl.root.scrollIntoView({
            block: "center",
            behavior: "smooth",
        });
    });
}

function downloadComplianceCsv(response) {
    const blob = new Blob([response.csv], {
        type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = response.fileName || "Compliance_Report.csv";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
}

function renderCompliancePrint(printWindow, response) {
    const metadata = response.metadata || {};
    const categories = (metadata.selectedCategories || [])
        .map(({name}) => name)
        .join(", ");
    const renderFieldRows = (row, fields) => fields.map(([field, label]) => `
        <tr>
            <th scope="row">${escapeHTML(label)}</th>
            <td>${escapeHTML(String(row[field] ?? ""))}</td>
        </tr>
    `).join("");
    const body = (response.rows || []).map((row, index) => `
        <section class="record">
            <h2>Report row ${index + 1}</h2>
            <div class="record-grid">
                <table>
                    <caption>Operational status and dates</caption>
                    <tbody>
                        ${renderFieldRows(row, OPERATIONAL_PRINT_FIELDS)}
                    </tbody>
                </table>
                <table>
                    <caption>Provenance, history, and evidence</caption>
                    <tbody>
                        ${renderFieldRows(row, PROVENANCE_PRINT_FIELDS)}
                    </tbody>
                </table>
            </div>
        </section>
    `).join("");
    printWindow.document.open();
    printWindow.document.write(`<!doctype html>
        <html><head><meta charset="utf-8"><title>Compliance Report</title>
        <style>
            @page{size:A4 landscape;margin:10mm}
            *{box-sizing:border-box}
            body{
                margin:0;
                color:#111827;
                font:10pt/1.4 Arial,sans-serif;
                background:#fff;
            }
            h1{margin:0;font-size:20pt;line-height:1.15}
            h2{margin:0 0 5pt;font-size:11pt}
            .report-header{
                margin-bottom:12pt;
                padding-bottom:8pt;
                border-bottom:2px solid #374151;
            }
            .meta{
                display:grid;
                grid-template-columns:repeat(3,minmax(0,1fr));
                gap:4pt 14pt;
                margin-top:7pt;
            }
            .meta-item{min-width:0}
            .meta-label{
                display:block;
                color:#4b5563;
                font-size:8.5pt;
                font-weight:700;
                text-transform:uppercase;
            }
            .meta-value{overflow-wrap:anywhere}
            .record{
                margin:0 0 12pt;
                break-inside:avoid;
                page-break-inside:avoid;
            }
            .record-grid{
                display:grid;
                grid-template-columns:minmax(0,1.12fr) minmax(0,0.88fr);
                gap:10pt;
                align-items:start;
            }
            table{
                width:100%;
                border-collapse:collapse;
                table-layout:fixed;
            }
            caption{
                border:1px solid #9ca3af;
                border-bottom:0;
                background:#e5e7eb;
                padding:4pt 6pt;
                text-align:left;
                font-weight:700;
            }
            th,td{
                border:1px solid #d1d5db;
                padding:4pt 6pt;
                text-align:left;
                vertical-align:top;
                overflow-wrap:anywhere;
            }
            th{
                width:34%;
                background:#f3f4f6;
                color:#374151;
                font-weight:700;
            }
            td{background:#fff}
            @media screen{
                body{padding:10mm}
            }
            @media print{
                body{-webkit-print-color-adjust:exact;print-color-adjust:exact}
            }
        </style></head><body>
        <header class="report-header">
            <h1>Compliance Report</h1>
            <div class="meta">
                <div class="meta-item">
                    <span class="meta-label">Generated by</span>
                    <span class="meta-value">${escapeHTML(metadata.generatedBy || "")}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Generated at</span>
                    <span class="meta-value">${escapeHTML(metadata.generatedAt || "")}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">As of</span>
                    <span class="meta-value">${escapeHTML(metadata.asOf || "")}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Business timezone</span>
                    <span class="meta-value">${escapeHTML(metadata.timezone || "")}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Categories</span>
                    <span class="meta-value">${escapeHTML(categories)}</span>
                </div>
                <div class="meta-item">
                    <span class="meta-label">Content</span>
                    <span class="meta-value">
                        History ${metadata.includeHistory ? "included" : "current only"};
                        not active ${metadata.includeInactive ? "included" : "excluded"}
                    </span>
                </div>
            </div>
        </header>
        <main>
            ${body || '<p>No report rows matched the selected criteria.</p>'}
        </main>
        <script>window.addEventListener("load",()=>window.print())<\/script>
        </body></html>`);
    printWindow.document.close();
}

/**
 * Opens the approved bounded report selector.
 * @param {object} options Report options.
 * @param {Array<object>} options.categories Authorized categories.
 * @param {"csv"|"print"} options.format Output format.
 * @returns {void}
 */
export function openComplianceReportModal({categories, format}) {
    const title = format === "csv" ?
        "Export Compliance CSV" :
        "Print Compliance report";
    const orderedCategories = [...categories].sort((left, right) =>
        (Number(left.order) || 0) - (Number(right.order) || 0) ||
        String(left.name || "").localeCompare(String(right.name || "")) ||
        String(left.id).localeCompare(String(right.id)));
    openStandardForm("compliance_report", null, {
        getFieldsHtml: () => `
            <div class="space-y-4" data-compliance-report-form>
                <div class="block text-sm font-medium text-gray-700">
                    Categories
                    <div class="mt-1" data-compliance-report-categories></div>
                    <span class="mt-1 block text-xs text-gray-500">
                        All authorized categories are selected by default.
                    </span>
                </div>
                <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                    <input type="checkbox" name="includeHistory"
                        class="${NATIVE_CHECKBOX_CLASSES}">
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                    <span>Include full manual record history</span>
                </label>
                <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
                    <input type="checkbox" name="includeInactive"
                        class="${NATIVE_CHECKBOX_CLASSES}">
                    <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                        <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
                    </span>
                    <span>Include not-active items and requirements</span>
                </label>
                <p class="text-sm text-gray-500 sm:text-xs"
                    data-native-field-skip="true">
                    Reports are generated from authoritative backend data and
                    reject selections above 10,000 rows.
                </p>
            </div>
        `,
        afterRender({itemForm}) {
            document.getElementById("form-title").textContent = title;
            const submit = itemForm.querySelector("button[type=submit]");
            if (submit) {
                submit.textContent = format === "csv" ?
                    "Download CSV" :
                    "Generate print view";
            }
            let selected = orderedCategories.map(({id}) => id);
            const categoryControl = createSelectControl({
                root: itemForm.querySelector(
                    "[data-compliance-report-categories]",
                ),
                name: "categoryIds",
                options: orderedCategories.map((category) => ({
                    value: category.id,
                    label: category.name,
                    order: category.order,
                })),
                value: selected,
                multiple: true,
                checkboxMenu: true,
                bulkSelection: true,
                searchable: true,
                searchThreshold: 7,
                required: true,
                requiredMessage:
                    "Select at least one Compliance category.",
                allSelectedLabel: "All authorized categories",
                emptySelectionLabel: "No categories selected",
                selectedCountLabel: "Selected categories",
                accessibleName: "Compliance report categories",
                portalMenu: true,
                onChange(value) {
                    selected = Array.isArray(value) ? value : [];
                },
            });
            bindNativeFieldBehavior(itemForm);
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const validation = validateRegisteredControls(itemForm);
                if (!validation.valid) {
                    focusReportCategoryError(categoryControl);
                    return;
                }
                const printWindow = format === "print" ?
                    openPrintWindow() :
                    null;
                if (format === "print" && !printWindow) {return;}
                if (submit) {
                    submit.disabled = true;
                    submit.textContent = "Generating...";
                }
                try {
                    const response = await generateComplianceReport({
                        categoryIds: selected,
                        format,
                        includeHistory:
                            itemForm.elements.includeHistory.checked,
                        includeInactive:
                            itemForm.elements.includeInactive.checked,
                    });
                    if (format === "csv") {
                        downloadComplianceCsv(response);
                    } else {
                        renderCompliancePrint(printWindow, response);
                    }
                    closeForm();
                } catch (error) {
                    printWindow?.close();
                    if (submit) {
                        submit.disabled = false;
                        submit.textContent = format === "csv" ?
                            "Download CSV" :
                            "Generate print view";
                    }
                    showReportFailure(error, submit);
                }
            };
        },
    });
}
